import React from 'react';

import Checkbox from '@lightspeed/cirrus-checkbox';
import Group from '@lightspeed/cirrus-group';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '@lightspeed/cirrus-modal';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';

import { createReactRenderer } from 'helpers/react-renderer';

import CustomerAccount from './CustomerAccount';
import DemoAccount from './DemoAccount';

const StatusComponent = {
  DemoAccount,
  CustomerAccount,
};

const storage = {
  gettingStarted: 'GETTING_STARTED',
};

const contact = {
  EU: {
    phone: '+31 (0)20 820 23 91 Ext. 6800',
    tel: '+310208202391',
    mailto: 'onboard.eu@lightspeedhq.com',
  },
  NA: {
    phone: '(866) 932-1801 Ext. 8000',
    tel: '8669321801',
    mailto: 'onboard.na@lightspeedhq.com',
  },
};

const link = {
  setupGuide: 'https://retail-support.lightspeedhq.com/hc/articles/360034468493',
  addInventoryData: 'https://retail-support.lightspeedhq.com/hc/articles/360036080494',
  configureHardware: 'https://retail-support.lightspeedhq.com/hc/articles/1260801415489',
  processSale: 'https://retail-support.lightspeedhq.com/hc/articles/1260800746850',
  trackPerformance: 'https://retail-support.lightspeedhq.com/hc/articles/228841987',
};

class GettingStarted extends React.Component {
  constructor(props) {
    super(props);

    // Check if the user previously closed the getting started modal during this session
    const showModal = !sessionStorage.getItem(storage.gettingStarted);

    this.state = {
      showModal: this.props.showModal,
      dontShowAgain: false,
    };

    this.status = ~['demo', 'customer'].indexOf(this.props.status) ? this.props.status : 'demo';

    this.handleChange = this.handleChange.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
  }

  handleChange(event) {
    this.setState({ dontShowAgain: event.target.checked });
  }

  handleCloseModal() {
    const { dontShowAgain } = this.state;

    // Do not display the modal during this session anymore
    // The modal was re-opening if going to the register (page reload)
    sessionStorage.setItem(storage.gettingStarted, true);

    // If the checkbox is not checked and the status is "setup", a request will update the status
    // to "tutorial". This changed status will prevent showing the firstStart wizard for subsequent logins.
    if (this.props.firstStartStatus === 'setup' || dontShowAgain) {
      window.doServerCall({
        form_name: 'tutorial',
        perm: 'on',
      });
    }

    this.setState({ showModal: false }, () => {
      setTimeout(() => {
        this.props.renderedComponent.unmount();
      }, 1000);
    });
  }

  render() {
    const { showModal, dontShowAgain } = this.state;
    const { employeeName, shopName, phoneWork } = this.props;
    const Component = StatusComponent[`${capitalize(this.status)}Account`];

    return (
      <Modal
        className={`cirrus getting-started ${this.status.toLowerCase()}-account`}
        isOpen={showModal}
        onRequestClose={this.handleCloseModal}
      >
        <ModalHeader onCloseClicked={this.handleCloseModal}>
          {this.status === 'demo'
            ? 'Welcome to Lightspeed Retail'
            : 'Getting started with Lightspeed Retail'}
        </ModalHeader>
        <ModalBody>
          <Component
            {...link}
            {...contact[this.props.infrastructure]}
            {...{ employeeName, shopName, phoneWork }}
          />
          <div style={{ margin: '0 -1.5rem' }}>
            <Divider />
          </div>
          <section className="cr-flex cr-justify-end cr-pt-4">
            <Checkbox
              checked={dontShowAgain}
              onChange={this.handleChange}
              label="Don&#39;t show again"
            />
          </section>
        </ModalBody>
        <ModalFooter>
          <Group className="cr-justify-end">
            <Button variant="primary" onClick={this.handleCloseModal}>
              Bring me to Lightspeed Retail
            </Button>
          </Group>
        </ModalFooter>
      </Modal>
    );
  }
}

GettingStarted.propTypes = {
  employeeName: PropTypes.string,
  shopName: PropTypes.string,
  phoneWork: PropTypes.string,
  infrastructure: PropTypes.oneOf(['NA', 'EU']),
  status: PropTypes.oneOf(['demo', 'customer', 'test', 'tmpdemo', 'delete']),
  firstStartStatus: PropTypes.oneOf(['setup', 'tutorial', 'done']).isRequired,
  showModal: PropTypes.bool,
};

GettingStarted.defaultProps = {
  employeeName: '',
  shopName: '',
  phoneWork: '',
  infrastructure: 'NA',
  status: 'customer',
  showModal: false,
};

const RenderedComponent = createReactRenderer({
  component: GettingStarted,
  selector: '[data-react-root="GettingStarted"]',
});

// Rendering this component with Cypress prevents from clicking elements under.
if (!window.Cypress) {
  RenderedComponent.mount();
}

export default GettingStarted;
